import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import CustomModal from "../Commaon/CustomModal";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";

const getRows = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const GroupSlotsList = () => {
  const [groups, setGroups] = useState([]);
  const [predefinedSlots, setPredefinedSlots] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [groupSlots, setGroupSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [addModal, setAddModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ groupId: "", slotId: "" });

  const showSnack = (message, type = "success") => {
    setSnack({ notification: { message, type } });
  };

  const loadGroups = async () => {
    try {
      const response = await AuthInterceptor("/admin/slot-group", {
        method: "GET",
      });
      const rows = getRows(response.data);
      setGroups(rows);
      if (!selectedGroupId && rows[0]?._id) {
        setSelectedGroupId(rows[0]._id);
        setForm((prev) => ({ ...prev, groupId: rows[0]._id }));
      }
    } catch (error) {
      setGroups([]);
    }
  };

  const loadPredefinedSlots = async () => {
    try {
      const response = await AuthInterceptor("/admin/slots", {
        method: "GET",
      });
      setPredefinedSlots(getRows(response.data));
    } catch (error) {
      setPredefinedSlots([]);
    }
  };

  const loadGroupSlots = async (groupId) => {
    if (!groupId) {
      setGroupSlots([]);
      return;
    }
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/group-slot/${groupId}`, {
        method: "GET",
      });
      setGroupSlots(getRows(response.data));
    } catch (error) {
      setGroupSlots([]);
      showSnack("Error fetching group slots", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
    loadPredefinedSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadGroupSlots(selectedGroupId);
    setForm((prev) => ({ ...prev, groupId: selectedGroupId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId]);

  const filteredRows = useMemo(() => {
    if (!query.trim()) return groupSlots;
    const search = query.toLowerCase();
    return groupSlots.filter(
      (item) =>
        item.slot?.startTime?.toLowerCase().includes(search) ||
        item.slot?.endTime?.toLowerCase().includes(search) ||
        item.startTime?.toLowerCase().includes(search) ||
        item.endTime?.toLowerCase().includes(search)
    );
  }, [groupSlots, query]);

  const addGroupSlot = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/group-slot", {
        method: "POST",
        body: JSON.stringify({
          groupId: form.groupId,
          slotId: form.slotId,
        }),
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack("Group slot added successfully");
        setAddModal(false);
        setForm({ groupId: selectedGroupId, slotId: "" });
        loadGroupSlots(selectedGroupId);
      } else {
        showSnack(response?.data?.m || "Error adding group slot", "error");
      }
    } catch (error) {
      showSnack("Error adding group slot", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (row) => {
    const nextStatus = !Boolean(row.isActive);
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/group-slot/${row._id}/toggle`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: nextStatus }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack("Group slot status updated");
        loadGroupSlots(selectedGroupId);
      } else {
        showSnack(response?.data?.m || "Error updating group slot", "error");
      }
    } catch (error) {
      showSnack("Error updating group slot", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteGroupSlot = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/group-slot/${deleteId}`, {
        method: "DELETE",
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack("Group slot deleted successfully");
        loadGroupSlots(selectedGroupId);
      } else {
        showSnack(response?.data?.m || "Error deleting group slot", "error");
      }
    } catch (error) {
      showSnack("Error deleting group slot", "error");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h2 className="mb-0">Group Slots</h2>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm"
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
            >
              {groups.map((group) => (
                <option key={group._id} value={group._id}>
                  {group.groupName || group.name || group._id}
                </option>
              ))}
            </select>
            <button className="btn btn-outline-custom btn-sm" onClick={() => setAddModal(true)}>
              Add Group Slot
            </button>
          </div>
        </div>

        <div className="input-group mb-3">
          <input className="form-control" placeholder="Search group slots..." value={query} onChange={(e) => setQuery(e.target.value)} />
          <button className="btn btn-outline-secondary">Search</button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Sr.</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Status</th>
                <th style={{ width: "180px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length > 0 ? (
                filteredRows.map((row, index) => (
                  <tr key={row._id || index}>
                    <td>{index + 1}</td>
                    <td>{row.slot?.startTime || row.startTime || "-"}</td>
                    <td>{row.slot?.endTime || row.endTime || "-"}</td>
                    <td>
                      <span className={`badge ${row.isActive ? "bg-success" : "bg-secondary"}`}>
                        {row.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => toggleStatus(row)}>
                        Toggle
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(row._id)}>
                        <CgTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    No group slots found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={addModal} onHide={() => setAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Group Slot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={addGroupSlot}>
            <div className="mb-3">
              <label className="form-label">Group</label>
              <select className="form-select" value={form.groupId} onChange={(e) => setForm((prev) => ({ ...prev, groupId: e.target.value }))}>
                {groups.map((group) => (
                  <option key={group._id} value={group._id}>
                    {group.groupName || group.name || group._id}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Slot ID</label>
              <select
                className="form-select"
                value={form.slotId}
                onChange={(e) => setForm((prev) => ({ ...prev, slotId: e.target.value }))}
              >
                <option value="">Select Slot</option>
                {predefinedSlots.map((slot) => (
                  <option key={slot._id} value={slot._id}>
                    {slot.startTime || "-"} - {slot.endTime || "-"}
                  </option>
                ))}
              </select>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setAddModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-outline-custom">
                Save
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <CustomModal
        show={Boolean(deleteId)}
        title="Confirm Deletion"
        message="Are you sure you want to delete this group slot?"
        onConfirm={deleteGroupSlot}
        onCancel={() => setDeleteId(null)}
      />
    </>
  );
};

export default GroupSlotsList;
